import type AgencesRepository from "@/core/administration/repositories/AgencesRepository";
import type { Agence, AgenceHoraires } from "@/core/administration/entities/Agence";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeRequest } from "../../http/services/executeRequest/executeRequest";
import agencesEndpoints from "../../api/agencesEndpoints/agencesEndpoints";

interface AdresseDto {
  ligne1?: string | null;
  ligne2?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  region?: string | null;
  pays?: string | null;
}

interface CoordonneesDto {
  latitude?: number | null;
  longitude?: number | null;
}

interface HorairesDto {
  horaireOuvertureOrdinaire?: string | null;
  horaireOuvertureSamedi?: string | null;
  horaireOuvertureDimanche?: string | null;
}

interface AgenceDto {
  code: string;
  nom: string;
  adresse?: AdresseDto | null;
  coordonnees?: CoordonneesDto | null;
  horaires?: HorairesDto | null;
  contact?: string | null;
}

interface PagedAgenceDto {
  items?: AgenceDto[];
  totalElements?: number;
}

function mapHoraires(dto: HorairesDto | null | undefined): AgenceHoraires | null {
  const ordinaire = dto?.horaireOuvertureOrdinaire ?? null;
  const samedi = dto?.horaireOuvertureSamedi ?? null;
  const dimanche = dto?.horaireOuvertureDimanche ?? null;
  if (!ordinaire && !samedi && !dimanche) {
    return null;
  }
  return { ordinaire, samedi, dimanche };
}

function mapAdresse(dto: AdresseDto | null | undefined): string | null {
  const lignes = [dto?.ligne1, dto?.ligne2].filter((ligne): ligne is string => !!ligne);
  return lignes.length ? lignes.join(", ") : null;
}

function mapDto(dto: AgenceDto): Agence {
  return {
    code: dto.code,
    nom: dto.nom,
    ville: dto.adresse?.ville ?? null,
    adresse: mapAdresse(dto.adresse),
    contact: dto.contact ?? null,
    latitude: dto.coordonnees?.latitude ?? null,
    longitude: dto.coordonnees?.longitude ?? null,
    horaires: mapHoraires(dto.horaires),
  };
}

export default class AgencesRepositoryBackend implements AgencesRepository {
  constructor(private readonly httpClient: HttpClient) {}

  listAgences(): Promise<RepositoryResult<Agence[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<PagedAgenceDto>>({
          url: agencesEndpoints.list(),
          withAuth: true,
        }),
      (paged) => (paged.items ?? []).map(mapDto),
    );
  }
}
